import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { useEarlyFreedomBaseline } from "web_myplann/Inspiration/Hooks/useEarlyFreedomBaseline";
import {
	computeEarlyFreedomContext,
	type EarlyFreedomInputs,
	type EarlyFreedomScenarioKey,
} from "web_myplann/Inspiration/Utils/earlyFreedomMath";
import { useHistoryEntryState } from "web_myplann/Shared/Hooks/useHistoryEntryState";

type EarlyFreedomJourneySnapshot = {
	inputs: EarlyFreedomInputs;
	selectedScenario: EarlyFreedomScenarioKey;
	scrollTop: number;
};

export type EarlyFreedomSheet = "quick" | "income" | null;

export const useEarlyFreedomJourney = () => {
	const { age, monthlySaving } = useEarlyFreedomBaseline();
	const { saved, save } = useHistoryEntryState<EarlyFreedomJourneySnapshot>(
		"earlyFreedomJourney",
	);
	const [inputs, setInputs] = useState<EarlyFreedomInputs>(saved?.inputs ?? {});
	const [activeSheet, setActiveSheet] = useState<EarlyFreedomSheet>(null);
	const [selectedScenario, setSelectedScenario] =
		useState<EarlyFreedomScenarioKey>(saved?.selectedScenario ?? "balanced");
	const scrollRef = useRef<HTMLDivElement>(null);
	const savedScrollTop = saved?.scrollTop;

	useLayoutEffect(() => {
		if (savedScrollTop) scrollRef.current?.scrollTo({ top: savedScrollTop });
	}, [savedScrollTop]);

	const context = useMemo(
		() => computeEarlyFreedomContext(inputs, { age, monthlySaving }),
		[inputs, age, monthlySaving],
	);

	const submitQuick = (targetExpenses: number) => {
		setInputs((prev) => ({ ...prev, targetExpenses }));
		setActiveSheet(null);
	};

	const submitIncome = (rentNet: number) => {
		setInputs((prev) => ({ ...prev, rentNet }));
		setActiveSheet(null);
	};

	const setTargetExpenses = (targetExpenses: number) =>
		setInputs((prev) => ({ ...prev, targetExpenses }));

	const setMonthlySaving = (monthlySaving: number) =>
		setInputs((prev) => ({ ...prev, monthlySaving }));

	const saveJourney = () =>
		save({
			inputs,
			selectedScenario,
			scrollTop: scrollRef.current?.scrollTop ?? 0,
		});

	return {
		context,
		inputs,
		activeSheet,
		openSheet: setActiveSheet,
		closeSheet: () => setActiveSheet(null),
		submitQuick,
		submitIncome,
		setTargetExpenses,
		setMonthlySaving,
		selectedScenario,
		setSelectedScenario,
		scrollRef,
		saveJourney,
	};
};
